import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EMAIL, INDIVIDUAL } from '@constants/constants';
import { logout } from './auth/authReducer';

interface UserDetail {
    id: string;
    tenantUUid: string;
    name: string;
    status: string;
    device_id: string;
    email_id: string;
    contact_id: string;
    create_date: string;
    address: string;
    userEmailStatus?: string;
}

interface UserDeviceList {
    id: string;
    name: string;
    date: string;
}

interface HospitalDetail {
    id: string;
    tenantUUid: string;
    device_id: string;
    hospital_id: string;
    hospital_name: string;
    hospital_branch: string;
    hospital_website: string;
    gst_no: string;
    contact_no: string;
    email_id: string;
    address: string;
    admin_name: string;
    mobile_no: string;
    admin_email: string;
    create_date: string;
    hospital_admin_status: string;
    logo: string | null;
    approvedOn: string | null;
}
interface DeviceDetail {
    device_uuid: string;
    device_id: string;
    device_name?: string;
    device_status: string;
    room_no?: string;
    bed_no?: string;
    status: string;
    created_at: string;
    created_by: string;
}

interface DeviceLog {
    id: string;
    device_id: string;
    device_name: string;
    created_at:string;
    created_by:string;
    role:string;
    time: string;
}
export interface ReportData {
    id: string;
    device_id: string;
    patient_name: string;
    medicine_name: string;
    created_at: string;
    time: string;
    status: string;
    nurse_name: string;
    rpm: string;
    re_saline: string;
    t_qua: string;
    createdAt_time: string;
}
interface ReportParams {
    device_id?: string;
    patient_name?: string;
    start_date: string;
    end_date: string;
}


interface DeviceCardDetail {
    id: string;
    nurse_id?: string;
    hasControl?: boolean;
    device_id?: string;
    device_name?: string;
    patient_name?: string;
    medicine_name?: string;
    assigned?: string;
    deviceStatus: string;
    infusion_rate: number;
    volume: number;
    volume_consumed: number;
    time_duration: number;
    battery_percentage: number;
    battery_status?: string;
    T_L_Time: number;
    T_Qua: number;
    Sel_Qua: number;
    RPM: number;
    status: string;
    error_code: string;
    created_at: string;
    created_at_time: string;
    created_by: string;
    bed_no: string;
}
interface NurseDetail {
    id: string;
    tenantUUid: string;
    name: string;
    status: string;
    deviceId?: string;
    hospitalId: string;
    hospitalName: string;
    email_id: string;
    contact_id: String;
    approvedBy: string;
    createdBy: string;
    createdAt: string;
    address: string;
    nurseId: string;
}

interface AuditLog {
    uuid: string;
    action: string;
    model_id: string;
    model_type: string;
    user_id: string;
    properties: Record<string, any>;
    host: string;
    timestamp: string;
    time?: string;
  }

interface UserState {
    userDetails: UserDetail | null;
    userDeviceList: UserDeviceList[] | null;
    hospitalDetails: HospitalDetail | null;
    deviceDetails: DeviceDetail | null;
    NurseDetails: NurseDetail | null;
    deviceCardDetails: DeviceCardDetail[] | null;
    deviceLog: DeviceLog | null;
    auditLog: AuditLog | null;
    email: string;
    name: string;
    password: string;
    deviceUniqueID: string;
    mobileNumber: string;
    address_one: string;
    address_two: string;
    city: string;
    country: string;
    pincode: string;
    otpType: string;
    showLink: boolean;
    file: any;
    goToDashboard: boolean;
    treeViewSelectedValue: string;
    accessToken: string;
    reportDatas: ReportData[] | null;
    reportParams: ReportParams | null;
    isToggleButtonActive:Boolean;
    pushNotificationToken: string;
}

const initialState: UserState = {
    userDetails: null,
    userDeviceList: null,
    hospitalDetails: null,
    reportDatas: null,
    reportParams: null,
    deviceDetails: null,
    NurseDetails: null,
    deviceCardDetails: null,
    deviceLog: null,
    auditLog: null,
    email: '',
    name: '',
    password: '',
    deviceUniqueID: '',
    mobileNumber: '',
    address_one: '',
    address_two: '',
    city: '',
    country: '',
    pincode: '',
    treeViewSelectedValue: '',
    accessToken: '',
    otpType: EMAIL,
    showLink: true,
    file: null,
    goToDashboard: false,
    isToggleButtonActive: false,
    pushNotificationToken: ''
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserDetail(state, action: PayloadAction<UserDetail>) {
            state.userDetails = action.payload;
        },
        setUserDeviceList(state, action: PayloadAction<UserDeviceList[]>) {
            state.userDeviceList = action.payload;
        },
        setHospitalDetail(state, action: PayloadAction<HospitalDetail>) {
            state.hospitalDetails = action.payload;
        },
        setReportData(state, action: PayloadAction<ReportData[]>) {
            state.reportDatas = action.payload;
        },
        setReportParams(state, action: PayloadAction<ReportParams>) {
            state.reportParams = action.payload;
        },
        setDeviceDetail(state, action: PayloadAction<DeviceDetail>) {
            state.deviceDetails = action.payload;
        },
        setNurseDetail(state, action: PayloadAction<NurseDetail>) {
            state.NurseDetails = action.payload;
        },
        setDeviceCardDetail(state, action: PayloadAction<DeviceCardDetail[]>) {
            state.deviceCardDetails = action.payload;
        },
        setDeviceLog(state, action: PayloadAction<DeviceLog>) {
            state.deviceLog = action.payload;
        },
        setAuditLog(state, action: PayloadAction<AuditLog>) {
            state.auditLog = action.payload;
        },
        setUserEmail(state, action: PayloadAction<string>) {
            state.email = action.payload;
        },
        setName(state, action: PayloadAction<string>) {
            state.name = action.payload;
        },
        setPassword(state, action: PayloadAction<string>) {
            state.password = action.payload;
        },
        setDeviceUniqueID(state, action: PayloadAction<string>) {
            state.deviceUniqueID = action.payload;
        },
        setUserMobileNumber(state, action: PayloadAction<string>) {
            state.mobileNumber = action.payload;
        },
        setAddressOne(state, action: PayloadAction<string>) {
            state.address_one = action.payload;
        },
        setAddressTwo(state, action: PayloadAction<string>) {
            state.address_two = action.payload;
        },
        setCity(state, action: PayloadAction<string>) {
            state.city = action.payload;
        },
        setCountry(state, action: PayloadAction<string>) {
            state.country = action.payload;
        },
        setPincode(state, action: PayloadAction<string>) {
            state.pincode = action.payload;
        },
        setOtpType(state, action: PayloadAction<string>) {
            state.otpType = action.payload;
        },
        setTreeViewSelectedValue(state, action: PayloadAction<string>) {
            state.treeViewSelectedValue = action.payload;
        },
        setAccessToken(state, action: PayloadAction<string>) {
            state.accessToken = action.payload;
        },
        setShowLink(state, action: PayloadAction<boolean>) {
            state.showLink = action.payload;
        },
        setGoToDashboard(state, action: PayloadAction<boolean>) {
            state.goToDashboard = action.payload;
        },
        setFile(state, action: PayloadAction<any>) {
            state.file = action.payload;
        },
        setIsToggleButtonActive(state, action: PayloadAction<Boolean>) {
            state.isToggleButtonActive = action.payload;
        },
        setPushNotificationToken(state, action: PayloadAction<string>) {
            state.pushNotificationToken = action.payload;
        },        
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => initialState);
    },
});

export const { setUserEmail, setGoToDashboard, setAuditLog, setAccessToken, setTreeViewSelectedValue,setIsToggleButtonActive, setDeviceLog, setPassword, setDeviceCardDetail, setFile, setDeviceUniqueID, setUserMobileNumber, setUserDeviceList, setOtpType, setUserDetail, setNurseDetail, setDeviceDetail, setHospitalDetail, setName, setShowLink, setAddressOne, setAddressTwo, setCity, setCountry, setPincode,setReportData, setReportParams, setPushNotificationToken } = userSlice.actions;

export default userSlice.reducer;